import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Popover,
  Radio,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import { useEffect, useState } from "react";
import { axiosInstance } from "../../../../utils/networking";
import { useViewport } from "../../../../utils/useViewport";
import {
  BroadcastFilter,
  CustomRecipient,
  MAX_CUSTOM_RECIPIENTS,
  PreviewBreakdown,
  RecipientSources,
  isValidRecipientEmail,
  parseCustomRecipient,
  splitRecipientInput,
} from "../broadcastTypes";
import cities from "../../../../assets/city";

interface SbcOption {
  _id: string;
  name: string;
}

const PRIMARY_INDUSTRY = [
  "Accommodations - Bed and breakfast, or any other lodging facility",
  "Business Services",
  "Construction and Maintenance Services - Grading, excavating, other construction and maintenance services",
  "Engineering, Design and Development",
  "Entertainment and Recreation",
  "Food",
  "Healthcare - chiropractors, dental, health clinics",
  "Maintenance & Repair - automobile repair, automobile dealership, cellular phone repair, landscaping and lawn maintenance",
  "Nonprofit - churches, religious centers, advocacy organizations, etc.",
  "Other",
  "Manufacturing",
  "Personal Services",
  "Professional & Other Services - taxes, consulting, publishing, marketing",
  "Retail",
  "Technology",
  "Transportation - limousine services, etc.",
];

const CLASSIFICATIONS = [
  "Woman-Owned",
  "Minority-Owned",
  "Veteran-Owned",
  "Disabled-Owned",
  "LGBTQ+ Owned",
  "Youth Owned",
  "Low to Moderate Income Owned",
  "Non-English Business",
  // "NEP", // hidden — replaced by GRP (Global Resource Program)
  "GRP",
];

const CERTIFICATIONS = [
  "Certified Women Owned",
  "Certified Minority Owned",
  "Certified Disabled Owned",
  "Certified Veteran Owned",
  "Certified LGBTQ+ Owned",
];

interface RecipientFilterProps {
  filter: BroadcastFilter;
  setFilter: (f: BroadcastFilter) => void;
  recipientCount: number | null;
  setRecipientCount: (n: number | null) => void;
  recipientSources: RecipientSources;
  setRecipientSources: (s: RecipientSources) => void;
  customRecipients: CustomRecipient[];
  setCustomRecipients: (r: CustomRecipient[]) => void;
}

/** Builds the "142 firm contacts + 12 custom − 1 duplicate = 150 recipients" line. */
const breakdownSentence = (b: PreviewBreakdown): string => {
  const terms: string[] = [];
  if (b.firmRecipients > 0)
    terms.push(
      `${b.firmRecipients.toLocaleString()} firm contact${b.firmRecipients !== 1 ? "s" : ""}`,
    );
  if (b.customValid > 0)
    terms.push(`${terms.length ? "+ " : ""}${b.customValid.toLocaleString()} custom`);
  if (b.duplicates > 0)
    terms.push(
      `− ${b.duplicates.toLocaleString()} duplicate${b.duplicates !== 1 ? "s" : ""}`,
    );
  if (b.suppressed > 0)
    terms.push(`− ${b.suppressed.toLocaleString()} unsubscribed/opted out`);

  const total = `${b.total.toLocaleString()} recipient${b.total !== 1 ? "s" : ""}`;
  const equation = terms.length ? `${terms.join(" ")} = ${total}` : total;
  return b.invalid > 0
    ? `${equation} (${b.invalid.toLocaleString()} invalid address${b.invalid !== 1 ? "es" : ""} skipped)`
    : equation;
};

const RecipientFilter = ({
  filter,
  setFilter,
  recipientCount,
  setRecipientCount,
  recipientSources,
  setRecipientSources,
  customRecipients,
  setCustomRecipients,
}: RecipientFilterProps) => {
  const { isPhone } = useViewport();
  // "All SBCs" / "Any time" show in the empty selects below. On a phone the
  // centred value would sit on the label, so the label moves to the border.
  const shrinkOnPhone = isPhone || undefined;
  const [previewing, setPreviewing] = useState(false);
  const [sbcs, setSbcs] = useState<SbcOption[]>([]);
  const [breakdown, setBreakdown] = useState<PreviewBreakdown | null>(null);
  const [chipInput, setChipInput] = useState("");
  const [capWarning, setCapWarning] = useState("");
  const [nameEditor, setNameEditor] = useState<{
    anchorEl: HTMLElement;
    index: number;
    value: string;
  } | null>(null);

  const firmsOn = recipientSources.firms;
  const customOn = recipientSources.custom;

  const validCustom = customRecipients.filter((r) => isValidRecipientEmail(r.email));
  const invalidCount = customRecipients.length - validCustom.length;

  // Any change to targeting invalidates the previewed count (existing pattern).
  const invalidatePreview = () => {
    setRecipientCount(null);
    setBreakdown(null);
  };

  const updateCustomRecipients = (next: CustomRecipient[]) => {
    setCustomRecipients(next);
    invalidatePreview();
  };

  // The two sources are mutually exclusive: a broadcast goes to filtered
  // client firms OR to a custom list, never both.
  const selectSource = (mode: "firms" | "custom") => {
    setRecipientSources({ firms: mode === "firms", custom: mode === "custom" });
    invalidatePreview();
  };

  /** Parses a typed/pasted blob into chips: de-duped, capped, invalid kept visible. */
  const commitInput = (raw: string) => {
    const tokens = splitRecipientInput(raw);
    if (tokens.length === 0) return;

    const next = [...customRecipients];
    const seen = new Set(next.map((r) => r.email.trim().toLowerCase()));
    let validSoFar = next.filter((r) => isValidRecipientEmail(r.email)).length;
    let hitCap = false;

    for (const token of tokens) {
      const parsed = parseCustomRecipient(token);
      if (!parsed) continue;
      const key = parsed.email.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      const valid = isValidRecipientEmail(parsed.email);
      if (valid && validSoFar >= MAX_CUSTOM_RECIPIENTS) {
        hitCap = true;
        continue;
      }
      if (valid) validSoFar += 1;
      seen.add(key);
      next.push(parsed);
    }

    setCapWarning(
      hitCap
        ? `Custom lists are capped at ${MAX_CUSTOM_RECIPIENTS} addresses — the extra entries weren't added.`
        : "",
    );
    setChipInput("");

    if (next.length !== customRecipients.length) {
      updateCustomRecipients(next);
    }
  };

  const removeRecipientAt = (index: number) => {
    setCapWarning("");
    updateCustomRecipients(customRecipients.filter((_, i) => i !== index));
  };

  const saveEditedName = () => {
    if (!nameEditor) return;
    const trimmed = nameEditor.value.trim();
    const next = customRecipients.map((r, i) =>
      i === nameEditor.index
        ? trimmed
          ? { ...r, name: trimmed }
          : { email: r.email }
        : r,
    );
    setNameEditor(null);
    updateCustomRecipients(next);
  };

  // Populate the SBC dropdown from the live users list instead of a static
  // hardcoded array, so every consultant (role bfc/admin) is selectable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("/user/allUsers");
        const users: any[] = res.data?.users || [];
        const options = users
          .filter((u) => u.role === "bfc" || u.role === "admin")
          .map((u) => ({
            _id: u._id,
            name: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
          }))
          .filter((o) => o.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        if (!cancelled) setSbcs(options);
      } catch (err) {
        console.error("Failed to load SBC list:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await axiosInstance.post("/broadcast/preview", {
        filter,
        recipientSources,
        customRecipients: customOn ? validCustom : [],
      });
      setRecipientCount(res.data.recipientCount);
      // `breakdown` is optional — an older server returns the count alone.
      setBreakdown(res.data.breakdown || null);
    } catch (err) {
      console.error("Preview error:", err);
    } finally {
      setPreviewing(false);
    }
  };

  const toggleArrayItem = (key: keyof BroadcastFilter, value: string) => {
    const current = (filter[key] as string[]) || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setFilter({ ...filter, [key]: updated });
    invalidatePreview();
  };

  const isChecked = (key: keyof BroadcastFilter, value: string) =>
    ((filter[key] as string[]) || []).includes(value);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Choose one recipient source — a broadcast goes to your filtered client
        firms <strong>or</strong> to a custom email list, not both.
      </Typography>

      {/* ── Source 1: client firms ───────────────────────────────── */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          p: 2,
          backgroundColor: firmsOn ? "#fff" : "#fafafa",
        }}
      >
        <FormControlLabel
          control={
            <Radio
              checked={firmsOn}
              onChange={() => selectSource("firms")}
              sx={{ color: "#9d2362", "&.Mui-checked": { color: "#9d2362" } }}
            />
          }
          label={
            <Typography variant="subtitle1" fontWeight={600}>
              Client firms
            </Typography>
          }
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2, ml: 4 }}
        >
          Target firms in the directory. Leaving every filter empty matches{" "}
          <strong>all firms</strong>.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            opacity: firmsOn ? 1 : 0.5,
          }}
        >
      {/* BFC */}
      <FormControl fullWidth disabled={!firmsOn}>
        <InputLabel shrink={shrinkOnPhone}>SBC Assignment</InputLabel>
        <Select
          value={filter.bfc || ""}
          label="SBC Assignment"
          displayEmpty
          onChange={(e) => {
            const id = e.target.value || undefined;
            const selected = sbcs.find((s) => s._id === id);
            setFilter({
              ...filter,
              bfc: id,
              bfcName: selected?.name || undefined,
            });
            invalidatePreview();
          }}
        >
          <MenuItem value="">
            <em>All SBCs</em>
          </MenuItem>
          {sbcs.map((sbc) => (
            <MenuItem key={sbc._id} value={sbc._id}>
              {sbc.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* City */}
      <FormControl fullWidth disabled={!firmsOn}>
        <InputLabel>Cities</InputLabel>
        <Select
          multiple
          value={filter.city || []}
          label="Cities"
          input={<OutlinedInput label="Cities" />}
          onChange={(e) => {
            setFilter({ ...filter, city: e.target.value as string[] });
            invalidatePreview();
          }}
          renderValue={(selected) => (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {(selected as string[]).map((v) => (
                <Chip key={v} label={v} size="small" />
              ))}
            </Stack>
          )}
        >
          {cities.map((city) => (
            <MenuItem key={city} value={city}>
              {city}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Business Type */}
      <FormControl fullWidth disabled={!firmsOn}>
        <InputLabel>Primary Industry</InputLabel>
        <Select
          multiple
          value={filter.bussType || []}
          label="Primary Industry"
          input={<OutlinedInput label="Primary Industry" />}
          onChange={(e) => {
            setFilter({ ...filter, bussType: e.target.value as string[] });
            invalidatePreview();
          }}
          renderValue={(selected) => (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {(selected as string[]).map((v) => (
                <Chip key={v} label={v} size="small" />
              ))}
            </Stack>
          )}
        >
          {PRIMARY_INDUSTRY.map((bt) => (
            <MenuItem key={bt} value={bt}>
              {bt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Classification */}
      <Box>
        <FormLabel component="legend">
          <Typography variant="body2" fontWeight={600}>
            Classification
          </Typography>
        </FormLabel>
        <FormGroup row>
          {CLASSIFICATIONS.map((c) => (
            <FormControlLabel
              key={c}
              control={
                <Checkbox
                  size="small"
                  disabled={!firmsOn}
                  checked={isChecked("classification", c)}
                  onChange={() => toggleArrayItem("classification", c)}
                />
              }
              label={<Typography variant="body2">{c}</Typography>}
            />
          ))}
        </FormGroup>
      </Box>

      {/* Certification */}
      <Box>
        <FormLabel component="legend">
          <Typography variant="body2" fontWeight={600}>
            Certification
          </Typography>
        </FormLabel>
        <FormGroup row>
          {CERTIFICATIONS.map((c) => (
            <FormControlLabel
              key={c}
              control={
                <Checkbox
                  size="small"
                  disabled={!firmsOn}
                  checked={isChecked("certification", c)}
                  onChange={() => toggleArrayItem("certification", c)}
                />
              }
              label={<Typography variant="body2">{c}</Typography>}
            />
          ))}
        </FormGroup>
      </Box>

      {/* Last Contact */}
      <FormControl fullWidth disabled={!firmsOn}>
        <InputLabel shrink={shrinkOnPhone}>Last Contact</InputLabel>
        <Select
          value={filter.lastContact || ""}
          label="Last Contact"
          displayEmpty
          onChange={(e) => {
            setFilter({ ...filter, lastContact: e.target.value || undefined });
            invalidatePreview();
          }}
        >
          <MenuItem value="">
            <em>Any time</em>
          </MenuItem>
          <MenuItem value="2weeks">Contacted within 2 weeks</MenuItem>
          <MenuItem value="1month">Contacted within 1 month</MenuItem>
          <MenuItem value="6months_plus">Not contacted in 6+ months</MenuItem>
          <MenuItem value="1year_plus">Not contacted in 1+ year</MenuItem>
        </Select>
      </FormControl>

        </Box>
      </Box>

      {/* ── Source 2: custom email list ──────────────────────────── */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          p: 2,
          backgroundColor: customOn ? "#fff" : "#fafafa",
        }}
      >
        <FormControlLabel
          control={
            <Radio
              checked={customOn}
              onChange={() => selectSource("custom")}
              sx={{ color: "#9d2362", "&.Mui-checked": { color: "#9d2362" } }}
            />
          }
          label={
            <Typography variant="subtitle1" fontWeight={600}>
              Custom email list
            </Typography>
          }
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2, ml: 4 }}
        >
          Type or paste addresses — separate them with commas, semicolons, spaces
          or new lines. Both <code>name@example.com</code> and{" "}
          <code>Name &lt;name@example.com&gt;</code> are accepted.
        </Typography>

        <Box sx={{ opacity: customOn ? 1 : 0.5 }}>
        <Autocomplete
          multiple
          freeSolo
          disableClearable
          disabled={!customOn}
          options={[] as CustomRecipient[]}
          value={customRecipients}
          inputValue={chipInput}
          filterOptions={(o) => o}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.email
          }
          onInputChange={(_e, value, reason) => {
            if (reason === "reset") {
              setChipInput("");
              return;
            }
            setChipInput(value);
          }}
          onChange={(_e, value) => {
            // freeSolo hands back raw strings for Enter-committed entries.
            const strings = value.filter(
              (v): v is string => typeof v === "string",
            );
            if (strings.length > 0) {
              commitInput(strings.join(","));
              return;
            }
            setCapWarning("");
            updateCustomRecipients(value as CustomRecipient[]);
          }}
          renderTags={(value, getTagProps) =>
            value.map((entry, index) => {
              const valid = isValidRecipientEmail(entry.email);
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={key}
                  size="small"
                  label={entry.name ? `${entry.name} <${entry.email}>` : entry.email}
                  color={valid ? "default" : "error"}
                  variant={valid ? "filled" : "outlined"}
                  onClick={(e) =>
                    valid
                      ? setNameEditor({
                          anchorEl: e.currentTarget,
                          index,
                          value: entry.name || "",
                        })
                      : undefined
                  }
                  onDelete={() => removeRecipientAt(index)}
                  title={
                    valid
                      ? "Click to set a display name"
                      : "Not a valid email address — this entry won't be sent"
                  }
                  sx={
                    valid
                      ? { cursor: "pointer" }
                      : {
                          textDecoration: "line-through",
                          "& .MuiChip-label": { textDecoration: "line-through" },
                        }
                  }
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                customRecipients.length === 0
                  ? "name@example.com, Another Name <another@example.com>"
                  : ""
              }
              onPaste={(e) => {
                const text = e.clipboardData.getData("text");
                if (!text) return;
                e.preventDefault();
                commitInput(`${chipInput}${chipInput ? "," : ""}${text}`);
              }}
              onKeyDown={(e) => {
                if (e.key === "," || e.key === ";") {
                  e.preventDefault();
                  commitInput(chipInput);
                }
              }}
              onBlur={() => {
                if (chipInput.trim()) commitInput(chipInput);
              }}
            />
          )}
        />

        <Typography
          variant="caption"
          color={invalidCount > 0 ? "error.main" : "text.secondary"}
          sx={{ display: "block", mt: 1 }}
        >
          {validCustom.length.toLocaleString()} valid ·{" "}
          {invalidCount.toLocaleString()} invalid
          {invalidCount > 0 ? " — invalid entries won't be sent" : ""}
        </Typography>
        </Box>

        {capWarning && (
          <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
            {capWarning}
          </Alert>
        )}

        {customOn && validCustom.length === 0 && (
          <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
            Add at least one valid address, or switch back to client firms.
          </Alert>
        )}

        <Popover
          open={!!nameEditor}
          anchorEl={nameEditor?.anchorEl || null}
          onClose={saveEditedName}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 2, width: 260 }}>
            <Typography variant="caption" color="text.secondary">
              Display name (used for the greeting)
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              value={nameEditor?.value || ""}
              onChange={(e) =>
                setNameEditor(
                  nameEditor ? { ...nameEditor, value: e.target.value } : null,
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveEditedName();
                }
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button size="small" onClick={saveEditedName} sx={{ color: "#9d2362" }}>
                Done
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>

      {/* Preview button + count */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="outlined"
          startIcon={
            previewing ? <CircularProgress size={16} /> : <PeopleIcon />
          }
          onClick={handlePreview}
          disabled={previewing}
          sx={{ borderColor: "#9d2362", color: "#9d2362" }}
        >
          Preview Recipients
        </Button>
        {recipientCount !== null && (
          <Alert
            severity={recipientCount === 0 ? "warning" : "info"}
            sx={{ py: 0.5 }}
          >
            {recipientCount === 0
              ? "No recipients match the current selection. Adjust your criteria."
              : breakdown
                ? breakdownSentence(breakdown)
                : `This broadcast will reach approximately ${recipientCount.toLocaleString()} recipients.`}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default RecipientFilter;
